import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Star, MoreVertical, FileText, Download, Loader2, Sparkles, Users, Send, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { companyId } = useCompany();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    favorites: true,
    financial: true,
    payroll: true,
    aiReports: true
  });
  const [activeCategory, setActiveCategory] = useState('standard');
  const [generatingReport, setGeneratingReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [emailingPayslip, setEmailingPayslip] = useState(null);

  const reportCategories = [
    { id: 'standard', label: 'Standard Reports' },
    { id: 'financial', label: 'Financial Reports' },
    { id: 'payroll', label: 'Payroll & Payslips' },
    { id: 'ai', label: 'AI-Powered Reports' }
  ];

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = companyId ? `?company_id=${companyId}` : '';
      const [typesRes, empRes] = await Promise.all([
        fetch(`${API_URL}/api/reports/types`, { credentials: 'include' }),
        fetch(`${API_URL}/api/payroll-full/employees${params}`, { credentials: 'include' })
      ]);
      if (typesRes.ok) {
        const types = await typesRes.json();
        const allReports = [];
        Object.entries(types).forEach(([cat, items]) => {
          items.forEach(r => allReports.push({ ...r, category: cat }));
        });
        setReports(allReports);
      }
      if (empRes.ok) setEmployees(await empRes.json());
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const generateReport = async (reportId) => {
    try {
      setGeneratingReport(reportId);
      const res = await fetch(`${API_URL}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ report_type: reportId, format: 'pdf', company_id: companyId })
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportId}_report.pdf`;
        a.click();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setGeneratingReport(null);
    }
  };

  const generatePayslip = async (employeeId) => {
    try {
      setGeneratingReport(employeeId);
      const res = await fetch(`${API_URL}/api/reports/payslip/${employeeId}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payslip_${employeeId}.pdf`;
        a.click();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Could not create the payslip.');
      }
    } catch (error) {
      console.error('Failed to generate payslip:', error);
    } finally {
      setGeneratingReport(null);
    }
  };

  const emailPayslip = async (employeeId) => {
    try {
      setEmailingPayslip(employeeId);
      const res = await fetch(`${API_URL}/api/reports/payslip/${employeeId}/email`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Payslip emailed to ${data.email}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (error) {
      console.error('Failed to email payslip:', error);
    } finally {
      setEmailingPayslip(null);
    }
  };

  const runAiReport = async () => {
    if (!aiQuery.trim()) return;
    try {
      setGeneratingReport('ai');
      const res = await fetch(`${API_URL}/api/reports/ai-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: aiQuery, company_id: companyId })
      });
      if (res.ok) {
        setAiResult(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (error) {
      console.error('AI report failed:', error);
    } finally {
      setGeneratingReport(null);
    }
  };

  const financialReports = reports.filter(r => r.category === 'financial');
  const payrollReports = reports.filter(r => r.category === 'payroll');

  const ReportItem = ({ report }) => (
    <div 
      className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded transition-colors cursor-pointer border-b last:border-b-0"
      onClick={() => generateReport(report.id)}
    >
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-700">{report.name}</span>
        {generatingReport === report.id && <Loader2 className="w-4 h-4 text-sky-600 animate-spin" />}
      </div>
      <Download className="w-4 h-4 text-gray-400 hover:text-sky-600" />
    </div>
  );

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Sidebar */}
      <div className="w-56 bg-slate-50 border-r overflow-y-auto">
        <div className="p-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Reports</h2>
          {reportCategories.map((cat) => (
            <button
              key={cat.id}
              className={`w-full text-left px-3 py-2 text-sm rounded transition-colors mb-1 ${
                activeCategory === cat.id ? 'bg-sky-100 text-sky-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-white overflow-y-auto p-6">
        {/* Search */}
        <div className="relative w-80 mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Financial Reports */}
            {(activeCategory === 'standard' || activeCategory === 'financial') && (
              <div className="mb-6">
                <button className="flex items-center gap-2 mb-3" onClick={() => toggleSection('financial')}>
                  {expandedSections.financial ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  <h3 className="text-lg font-semibold text-gray-900">Financial Reports</h3>
                </button>
                {expandedSections.financial && (
                  <div className="bg-white border rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      {financialReports.map((report) => <ReportItem key={report.id} report={report} />)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payroll & Payslips */}
            {(activeCategory === 'standard' || activeCategory === 'payroll') && (
              <div className="mb-6">
                <button className="flex items-center gap-2 mb-3" onClick={() => toggleSection('payroll')}>
                  {expandedSections.payroll ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  <h3 className="text-lg font-semibold text-gray-900">Payroll & Employee Payslips</h3>
                </button>
                {expandedSections.payroll && (
                  <div className="bg-white border rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {payrollReports.map((report) => <ReportItem key={report.id} report={report} />)}
                    </div>
                    
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2"><Users className="w-4 h-4" />Employee Payslips</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {employees.length === 0 ? (
                        <p className="text-sm text-gray-500">No employees found</p>
                      ) : (
                        employees.map((emp) => (
                          <div key={emp.employee_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm font-medium">{emp.name || [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.employee_id}</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => generatePayslip(emp.employee_id)}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded hover:bg-sky-200"
                                disabled={generatingReport === emp.employee_id}
                              >
                                {generatingReport === emp.employee_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                PDF
                              </button>
                              <button
                                onClick={() => emailPayslip(emp.employee_id)}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                                disabled={emailingPayslip === emp.employee_id}
                              >
                                {emailingPayslip === emp.employee_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                Email
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI-Powered Reports */}
            {(activeCategory === 'standard' || activeCategory === 'ai') && (
              <div className="mb-6">
                <button className="flex items-center gap-2 mb-3" onClick={() => toggleSection('aiReports')}>
                  {expandedSections.aiReports ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />AI-Powered Reports
                  </h3>
                </button>
                {expandedSections.aiReports && (
                  <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-3">Ask questions in natural language to generate custom reports:</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g., Show me revenue analysis, What are my top expenses..."
                        value={aiQuery}
                        onChange={(e) => setAiQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runAiReport()}
                        className="flex-1 px-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      />
                      <button
                        onClick={runAiReport}
                        disabled={generatingReport === 'ai'}
                        className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 flex items-center gap-2"
                      >
                        {generatingReport === 'ai' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Generate
                      </button>
                    </div>
                    
                    {aiResult && (
                      <div className="mt-4 p-4 bg-white rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-800">{aiResult.report_type}</h4>
                          <button onClick={() => setAiResult(null)} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
                        </div>
                        
                        {aiResult.data && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            {Object.entries(aiResult.data).map(([key, value]) => (
                              <div key={key} className="p-3 bg-slate-50 rounded">
                                <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                                <p className="text-lg font-bold text-gray-800">
                                  {typeof value === 'number' ? (value > 1000 ? `$${value.toLocaleString()}` : value) : 
                                   typeof value === 'object' ? JSON.stringify(value).substring(0, 30) : value}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {aiResult.insights && (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-700">Insights:</p>
                            {aiResult.insights.map((insight, i) => (
                              <p key={i} className="text-sm text-gray-600 flex items-start gap-2">
                                <span className="text-emerald-500">•</span>{insight}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
